import React, { FC, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { Incrementer } from '@components';
import { getUnitPrice, toCurrency } from '@helpers';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { useGetAllProductsQuery, useGetBulkPricingRulesQuery } from '@services';
import { updateProduct } from '@store/slices/order';
import { usePrevious } from 'react-use';

type Props = {
  productId: number;
  quantity: number;
};

export const CartItem: FC<Props> = ({ productId, quantity }) => {
  const [discount, setDiscount] = useState<number | undefined>();
  const { data: products } = useGetAllProductsQuery();
  const { data: bulkPricingRules } = useGetBulkPricingRulesQuery(productId);

  const product = products?.find((prod) => prod.id === productId);

  const cartItems = useAppSelector((state) => state.order.value.products);
  const { selectedPaymentMethod } = useAppSelector((state) => state.order.value);
  const previousPaymentMethod: any = usePrevious(selectedPaymentMethod);

  const productPrice = cartItems?.find((item: any) => item.productId === productId)?.unitPrice;

  const productUnitQtyPrice = getUnitPrice(quantity, productPrice, bulkPricingRules) * quantity * (1 - (discount || 0) / 100);

  const dispatch = useAppDispatch();

  const handleChange = (newValue: number) => {
    if (!product) return;

    dispatch(
      updateProduct({
        productId,
        quantity: newValue,
        unitPrice: getUnitPrice(newValue, productPrice, bulkPricingRules),
      }),
    );
  };

  useEffect(() => {
    if (selectedPaymentMethod?.id === 'noCharge') {
      handleDiscountChange('100');
    } else if (previousPaymentMethod?.id === 'noCharge' && selectedPaymentMethod?.id !== 'noCharge') {
      handleDiscountChange(undefined);
    }
  }, [selectedPaymentMethod]);

  const handleDiscountChange = (newValue: string | undefined) => {
    const valueAsNumber = Number(newValue);
    const newDiscount = valueAsNumber > 100 ? 100 : valueAsNumber < 0 ? 0 : valueAsNumber;

    setDiscount((val) => (newValue === undefined ? 0 : Number(newDiscount)));

    dispatch(
      updateProduct({
        productId,
        discountPercentage: newDiscount / 100,
      }),
    );
  };

  return (
    <Box width="100%" data-testid="cart-item">
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize="18px" data-testid="product-name">
          {product?.name}
        </Typography>
        <Typography fontSize="20px" fontWeight="bold" data-testid="product-price">
          {product !== undefined && toCurrency(productUnitQtyPrice)}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Incrementer id={`${productId}-incrementer`} quantity={quantity} onChange={handleChange} />
        <TextField
          sx={{ width: 106 }}
          size="small"
          type="number"
          value={discount}
          defaultValue={discount}
          InputLabelProps={{ shrink: true }}
          onChange={({ target: { value } }) => handleDiscountChange(value)}
          label="Discount %"
          disabled={selectedPaymentMethod?.id === 'noCharge'}
          data-testid="product-discount"
        />
        <Button onClick={() => handleChange(0)} data-testid="cart-item-remove-btn">
          Remove
        </Button>
      </Box>
    </Box>
  );
};
